/**
 * Popup renderer — the lead-capture form a visitor sees over the page.
 *
 * Lives in `widget-bridge` (not `apps/widget`) so the dashboard editor can
 * mount the exact same renderer for its live preview. A preview that was
 * re-implemented would drift from what visitors get, which is the one
 * thing a preview exists to prevent.
 *
 * Pure DOM + a CSS string, same shape as `panel.ts` / `teaser.ts` in
 * `apps/widget` — no framework, no network. Everything the popup needs is
 * on {@link MountPopupOptions.popup}; the host owns transport and decides
 * what "submit" does.
 *
 * Two layouts, from `design.layout`:
 *
 *   - `stacked` — one column, image as a banner above the pitch.
 *   - `split`   — image beside the content, which is what makes a lead popup
 *                 read like a designed landing panel rather than a form in a
 *                 box. The media column is decorative; the heading and
 *                 description stay in the content column so the mobile
 *                 breakpoint (image collapses to a banner) never hides the
 *                 pitch. `split` with no usable `imageUrl` degrades to
 *                 `stacked`.
 */

import type { PopupDesign, PopupField, PopupPublicDto } from '@prochat/shared'

export type PopupScreen = 'main' | 'form' | 'nudge' | 'thanks'

export interface PopupSubmitValues {
  [fieldKey: string]: string
}

export interface PopupFieldError {
  fieldKey: string
  message: string
}

export interface MountPopupOptions {
  popup: PopupPublicDto
  /** Fired when the visitor submits a valid form. */
  onSubmit: (values: PopupSubmitValues) => void | Promise<void>
  /** Secondary button set to `open_chat`. */
  onOpenChat?: () => void
  /** Secondary button set to `open_url`; the host decides how to navigate. */
  onOpenUrl?: (url: string) => void
  /** × button, backdrop click (when enabled), or a close action. */
  onClose?: () => void
}

export interface PopupController {
  show(): void
  hide(): void
  /**
   * Advance to the thank-you screen. `mountPopup` calls this itself once
   * `onSubmit` resolves; it is exposed for hosts that submit out of band.
   */
  showThanks(): void
  /** Current screen — the dashboard preview asserts on this. */
  screen(): PopupScreen
  /** Per-field errors from the last submit attempt. */
  errors(): PopupFieldError[]
  destroy(): void
}

/**
 * Vietnamese mobile numbers only. Deliberately narrow: this form feeds a
 * sales call-back list, and a number that will not dial costs more than a
 * stricter check does.
 */
const VN_PHONE_RE = /^(?:\+84|84|0)(?:3|5|7|8|9)\d{8}$/

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

/**
 * How long the form may sit untouched before it turns into a nudge.
 *
 * Measured from the LAST keystroke, not from when the popup opened. Someone
 * halfway through typing a phone number is still engaged, and a clock started
 * at paint would fire on top of them and throw away what they typed — so the
 * window is re-armed on every `input` and only counts real silence.
 */
const NUDGE_IDLE_MS = 40_000

/**
 * Narrowest a `split` card is allowed to render. The stacked default (420px)
 * gives each of the two columns ~200px, which collapses the inputs; the
 * editor states this floor next to the layout picker.
 */
export const SPLIT_MIN_WIDTH = 720

export function validatePopupField(field: PopupField, raw: string): string | null {
  const value = raw.trim()
  if (field.required && !value) return 'Vui lòng nhập thông tin này.'
  if (!value) return null
  if (field.type === 'phone' && !VN_PHONE_RE.test(value.replace(/[\s.\-()]/g, ''))) {
    return 'Dạ, hình như số điện thoại không đúng rồi, anh chị cho em xin lại sdt đúng với ạ.'
  }
  if (field.type === 'email' && !EMAIL_RE.test(value)) {
    return 'Email chưa đúng, vui lòng nhập lại.'
  }
  if (field.type === 'select' && field.options?.length && !field.options.includes(value)) {
    return 'Vui lòng chọn một trong các tuỳ chọn.'
  }
  return null
}

/** Strips the visual separators owners type into phone fields. */
export function normalizePhoneValue(raw: string): string {
  return raw.replace(/[\s.\-()]/g, '')
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Attach an alpha to a brand colour.
 *
 * Appending `33` to `#1B5E3F` works, but `design.primary` comes from a
 * tenant-editable field and may be stored as `rgb(…)` or a named colour —
 * `rgb(27, 94, 63)33` is invalid CSS, and a single broken declaration in the
 * generated sheet silently drops the rule it sits in. Anything that is not
 * hex comes back unchanged, which degrades to "no tint" rather than "no
 * border-radius".
 */
function withAlpha(color: string, alpha: number): string {
  const value = color.trim()
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value)
  if (!match) return value
  let hex = match[1]
  if (hex.length === 3) hex = hex.split('').map((c) => c + c).join('')
  const n = parseInt(hex, 16)
  const clamped = Math.min(1, Math.max(0, alpha))
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${clamped})`
}

/**
 * The select chevron, baked as a data URI so the control does not depend on
 * a caret character (which renders differently, or not at all, across the
 * system UI fonts). Themed from `text` so it matches the labels beside it.
 */
function selectChevron(textColor: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${textColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}


/**
 * Only http(s) links may reach `href`/`window.open` — a `javascript:` URL
 * in a tenant-authored popup would run in the visitor's page origin.
 */
export function sanitizePopupUrl(raw: string): string | null {
  const value = raw.trim()
  if (!value) return null
  try {
    const parsed = new URL(value, 'https://placeholder.invalid')
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null
    return value
  } catch {
    return null
  }
}

function popupCss(design: PopupDesign): string {
  const align =
    design.position === 'center'
      ? 'align-items: center; justify-content: center;'
      : design.position === 'bottom-left'
        ? 'align-items: flex-end; justify-content: flex-start;'
        : 'align-items: flex-end; justify-content: flex-end;'
  const split = design.layout === 'split'
  const primary = design.primary
  // A two-column card cannot fit in the stacked default: 420px leaves ~200px
  // per column, so the inputs collapse and the picture turns into a sliver.
  // Lift the width when the tenant picked `split` but left the default — a
  // width they raised themselves is theirs to keep.
  const cardWidth = split ? Math.max(design.width, SPLIT_MIN_WIDTH) : design.width
  // Tints derived from the brand colour so a tenant that only picks one
  // colour still gets a coherent sheet instead of a saturated slab.
  const ring = withAlpha(primary, 0.2)
  const wash = withAlpha(primary, 0.08)
  const controlRadius = Math.max(0, design.radius - 4)

  return `
:host { all: initial; }
.pc-popup-backdrop {
  position: fixed; inset: 0; z-index: 2147483000;
  display: flex; ${align}
  padding: 20px; box-sizing: border-box;
  ${design.backdropDim ? 'background: rgba(15, 23, 42, 0.55);' : 'background: transparent;'}
  font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  animation: pc-popup-fade 180ms ease-out;
}
@keyframes pc-popup-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes pc-popup-rise { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
.pc-popup-card {
  position: relative;
  width: ${cardWidth}px; max-width: 100%; max-height: 90vh; overflow-y: auto;
  background: #fff; color: ${design.text};
  border-radius: ${design.radius}px; font-size: ${design.fontSize}px;
  box-shadow: 0 30px 70px rgba(15, 23, 42, 0.32), 0 2px 8px rgba(15, 23, 42, 0.12);
  animation: pc-popup-rise 220ms cubic-bezier(0.16, 1, 0.3, 1); box-sizing: border-box;
}
.pc-popup-main { padding: ${split ? '30px 26px 22px' : '28px 24px 20px'}; text-align: center; }
.pc-popup-image { width: 100%; max-height: 160px; object-fit: cover; border-radius: ${controlRadius}px; margin-bottom: 16px; display: block; }
.pc-popup-heading { margin: 0 0 8px; font-size: ${design.fontSize + 5}px; line-height: 1.3; font-weight: 700; color: ${design.text}; }
.pc-popup-description { margin: 0; color: ${design.text}; opacity: 0.78; line-height: 1.55; white-space: pre-line; }
.pc-popup-sub { margin: 0 0 16px; color: ${design.text}; opacity: 0.78; line-height: 1.55; white-space: pre-line; text-align: left; }
.pc-popup-form { padding: 8px 24px 24px; }
.pc-popup-field { margin-bottom: 14px; text-align: left; }
.pc-popup-label { display: block; margin-bottom: 6px; font-weight: 600; font-size: ${design.fontSize - 1}px; }
.pc-popup-required { color: #dc2626; margin-left: 2px; }
.pc-popup-input, .pc-popup-select, .pc-popup-textarea {
  width: 100%; box-sizing: border-box; padding: 10px 12px;
  border: 1px solid #d1d5db; border-radius: ${controlRadius}px;
  font-size: ${design.fontSize}px; font-family: inherit; color: ${design.text};
  background: #fff;
}
.pc-popup-input:focus, .pc-popup-select:focus, .pc-popup-textarea:focus {
  outline: none; border-color: ${primary}; box-shadow: 0 0 0 3px ${ring};
}
.pc-popup-textarea { min-height: 84px; resize: vertical; }
.pc-popup-select {
  -webkit-appearance: none; appearance: none; cursor: pointer;
  background-image: ${selectChevron(design.text)};
  background-repeat: no-repeat; background-position: right 12px center; background-size: 15px 15px;
  padding-right: 36px;
}
.pc-popup-error { display: block; margin-top: 5px; color: #dc2626; font-size: ${design.fontSize - 2}px; }
.pc-popup-actions { display: flex; gap: 10px; margin-top: 18px; flex-wrap: wrap; }
.pc-popup-submit {
  flex: 1 1 160px; padding: 13px 18px; border: 0; cursor: pointer;
  border-radius: ${controlRadius}px;
  background: ${primary}; color: #fff; font-size: ${design.fontSize}px; font-weight: 600; font-family: inherit;
}
.pc-popup-submit:hover { filter: brightness(0.92); }
.pc-popup-submit[disabled] { opacity: 0.7; cursor: not-allowed; }
.pc-popup-secondary {
  flex: 0 1 auto; padding: 13px 18px; cursor: pointer;
  border: 1px solid ${primary}; border-radius: ${controlRadius}px;
  background: transparent; color: ${primary}; font-size: ${design.fontSize}px; font-weight: 600; font-family: inherit;
}
.pc-popup-secondary:hover { background: ${wash}; }
.pc-popup-close {
  position: absolute; top: 10px; right: 12px; z-index: 2;
  background: none; border: 0; cursor: pointer; font-size: 24px; line-height: 1;
  color: ${design.text}; opacity: 0.5; padding: 4px 8px;
}
.pc-popup-close:hover { opacity: 1; }
.pc-popup-thanks { padding: 40px 28px; text-align: center; }
.pc-popup-thanks .pc-popup-heading { color: ${primary}; }

/* ---- split: image beside the content (layout: 'split') ----
   The media column is decoration, so it carries no text a visitor has to
   read: the heading and description stay in the content column, where they
   survive the mobile collapse below. */
.pc-popup-card.pc-popup--split {
  display: grid;
  grid-template-columns: minmax(0, 1.06fr) minmax(0, 0.94fr);
  overflow: hidden;
}
.pc-popup--split > .pc-popup-media {
  position: relative; overflow: hidden; min-height: 320px;
  background: ${primary};
}
.pc-popup--split > .pc-popup-media > img {
  position: absolute; inset: 0; width: 100%; height: 100%;
  object-fit: cover; display: block;
}
.pc-popup--split > .pc-popup-media::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(155deg, ${withAlpha(primary, 0.28)} 0%, ${withAlpha(primary, 0.52)} 55%, ${withAlpha(primary, 0.78)} 100%);
}
.pc-popup--split > .pc-popup-body {
  overflow-y: auto; max-height: 90vh; box-sizing: border-box;
}
.pc-popup--split .pc-popup-close {
  color: #fff; opacity: 0.85;
  background: rgba(15, 23, 42, 0.35);
  border-radius: 999px; width: 34px; height: 34px;
  display: grid; place-items: center; padding: 0;
  font-size: 22px;
}
.pc-popup--split .pc-popup-close:hover { opacity: 1; }
.pc-popup--split .pc-popup-main { text-align: left; }
.pc-popup--split .pc-popup-form { padding: 4px 24px 24px; }

@media (max-width: 640px) {
  .pc-popup-card { width: 100%; }
  .pc-popup-heading { font-size: ${design.fontSize + 3}px; }
  .pc-popup-card.pc-popup--split { grid-template-columns: 1fr; }
  .pc-popup--split > .pc-popup-media {
    order: 1; min-height: 140px; max-height: 140px;
  }
  .pc-popup--split > .pc-popup-body {
    order: 2; overflow-y: visible; max-height: none;
  }
  /* The × sits on the image here, so it must not scroll away with the form. */
  .pc-popup--split > .pc-popup-media .pc-popup-close { position: absolute; }
}
`
}


function buildField(field: PopupField): HTMLElement {
  const wrap = document.createElement('div')
  wrap.className = 'pc-popup-field'
  wrap.dataset.fieldKey = field.key

  const label = document.createElement('label')
  label.className = 'pc-popup-label'
  label.setAttribute('for', `pc-popup-${field.id}`)
  label.textContent = field.label
  if (field.required) {
    const star = document.createElement('span')
    star.className = 'pc-popup-required'
    star.textContent = '*'
    label.appendChild(star)
  }
  wrap.appendChild(label)

  const inputId = `pc-popup-${field.id}`
  if (field.type === 'select') {
    const select = document.createElement('select')
    select.className = 'pc-popup-select'
    select.id = inputId
    select.name = field.key
    const blank = document.createElement('option')
    blank.value = ''
    blank.textContent = field.placeholder ?? 'Chọn...'
    select.appendChild(blank)
    for (const option of field.options ?? []) {
      const el = document.createElement('option')
      el.value = option
      el.textContent = option
      select.appendChild(el)
    }
    wrap.appendChild(select)
  } else {
    const input = document.createElement('input')
    input.className = 'pc-popup-input'
    input.id = inputId
    input.name = field.key
    input.type = field.type === 'phone' ? 'tel' : field.type === 'email' ? 'email' : 'text'
    input.autocomplete = field.type === 'email' ? 'email' : field.type === 'phone' ? 'tel' : 'on'
    if (field.placeholder) input.placeholder = field.placeholder
    wrap.appendChild(input)
  }

  const error = document.createElement('span')
  error.className = 'pc-popup-error'
  error.hidden = true
  wrap.appendChild(error)
  return wrap
}

/**
 * Read the form values keyed by field key. Phone values are stripped of
 * separators so the API normalizes once, from one shape.
 *
 * Walks the field wrappers by their `data-field-key` rather than querying
 * `[name="…"]`: a field key comes from tenant config and may contain
 * characters that need CSS escaping, which `CSS.escape` would handle only
 * where that global exists — it does not in jsdom, and not in older
 * browsers either.
 */
function readValues(root: ParentNode, fields: PopupField[]): PopupSubmitValues {
  const byKey = new Map(fields.map((field) => [field.key, field]))
  const values: PopupSubmitValues = {}
  for (const wrap of Array.from(root.querySelectorAll('.pc-popup-field'))) {
    const key = (wrap as HTMLElement).dataset.fieldKey
    const field = key ? byKey.get(key) : undefined
    if (!key || !field) continue
    const el = wrap.querySelector('input, select, textarea') as
      | HTMLInputElement
      | HTMLSelectElement
      | null
    if (!el) continue
    const raw = el.value ?? ''
    values[key] = field.type === 'phone' ? normalizePhoneValue(raw) : raw.trim()
  }
  return values
}

/**
 * Read the form field list. `design.fields` is the storage location the
 * editor writes to; the renderer filters defensively so a config that
 * predates fields (or was hand-edited into junk) still renders the main
 * screen instead of throwing.
 */
export function popupFieldsOf(popup: PopupPublicDto): PopupField[] {
  const fields = popup.design?.fields
  if (!Array.isArray(fields)) return []
  return fields.filter((f) => f && typeof f.key === 'string' && f.key.length > 0)
}

export function mountPopup(
  host: ShadowRoot | HTMLElement,
  opts: MountPopupOptions,
): PopupController {
  const { popup } = opts
  const design = popup.design
  const fields = popupFieldsOf(popup)

  let destroyed = false
  // `startOnForm` opens straight into the form so the visitor fills it without
  // an extra "Để lại thông tin" click; a popup with no fields has nothing to
  // fill, so it stays on `main` and its CTA routes to chat.
  const initialScreen = (): PopupScreen =>
    design.startOnForm && fields.length > 0 ? 'form' : 'main'
  let current: PopupScreen = initialScreen()
  let lastErrors: PopupFieldError[] = []
  let backdrop: HTMLDivElement | null = null
  let nudgeTimer: ReturnType<typeof setTimeout> | null = null

  /** Cancels a pending nudge. Every path off the form runs through `teardown`,
   *  which calls this, so a timer can never outlive the screen it was armed on. */
  function clearNudge(): void {
    if (nudgeTimer === null) return
    clearTimeout(nudgeTimer)
    nudgeTimer = null
  }

  /**
   * Has the visitor already handed over the thing the form exists to collect?
   *
   * "Anh chị có còn đó không ạ?" is a prompt to come back and type. Once every
   * name/number box holds something we can actually dial, the silence is on
   * our side — nudging then would read as impatience about a form already sent.
   * Validity, not emptiness: someone halfway through a phone number has not
   * handed it over, and they are precisely who the nudge is for.
   */
  function alreadySuppliedContact(): boolean {
    const scope = backdrop
    if (!scope) return false
    const contact = Array.from(scope.querySelectorAll<HTMLElement>('.pc-popup-field')).filter(
      (wrap) => {
        const field = fields.find((f) => f.key === wrap.dataset.fieldKey)
        return field?.type === 'phone' || field?.type === 'text'
      },
    )
    return contact.every((wrap) => {
      const field = fields.find((f) => f.key === wrap.dataset.fieldKey)!
      const input = wrap.querySelector('input, textarea') as HTMLInputElement | null
      const value = input?.value.trim() ?? ''
      return value.length > 0 && validatePopupField(field, value) === null
    })
  }

  /**
   * (Re)starts the 40s silence window from zero. Bound to `input` as well as
   * called on paint, so the countdown is always measured from the last
   * keystroke rather than from when the form appeared.
   */
  function armNudge(): void {
    clearNudge()
    nudgeTimer = setTimeout(() => {
      nudgeTimer = null
      // `current` guards a timer armed just before a submit, which must not
      // fire the nudge over the thank-you screen.
      if (destroyed || current !== 'form' || alreadySuppliedContact()) return
      current = 'nudge'
      paint()
    }, NUDGE_IDLE_MS)
  }

  const style = document.createElement('style')
  style.textContent = popupCss(design)
  host.appendChild(style)

  function teardown(): void {
    clearNudge()
    if (backdrop?.parentNode) backdrop.parentNode.removeChild(backdrop)
    backdrop = null
  }

  /** Every dismissal path — ×, backdrop click, secondary `close` — runs
   *  through here, so the overlay can never outlive the close event it
   *  reported. */
  function close(): void {
    teardown()
    opts.onClose?.()
  }

  /**
 * The decorative half of a `split` popup.
 *
 * Deliberately carries no text: the heading and description live in the
 * content column, where the mobile media banner (see the CSS breakpoint)
 * cannot swallow them. Renders nothing when no image is configured.
 */
function renderMedia(design: PopupDesign): HTMLDivElement | null {
  if (!design.main.imageUrl) return null
  const safeImage = sanitizePopupUrl(design.main.imageUrl)
  if (!safeImage) return null

  const media = document.createElement('div')
  media.className = 'pc-popup-media'
  media.setAttribute('aria-hidden', 'true')

  const img = document.createElement('img')
  img.src = safeImage
  img.alt = ''
  img.loading = 'lazy'
  img.decoding = 'async'
  // A tenant-configured image is a third-party URL; never hand it the
  // visitor's page URL or a Referer header they did not ask to send.
  img.referrerPolicy = 'no-referrer'
  // The overlay is painted by the parent's ::after, so a broken or absent
  // image still leaves a branded panel rather than a hole.
  media.appendChild(img)
  return media
}

/**
 * Wrap content in the popup card, adding the media column when the design
 * asks for `split`. Returns the card so callers can append the × button.
 */
function buildCard(design: PopupDesign): HTMLDivElement {
  const card = document.createElement('div')
  card.className = 'pc-popup-card'

  const split = design.layout === 'split'
  const media = split ? renderMedia(design) : null
  if (media) card.classList.add('pc-popup--split')

  // Content before media in the DOM: a screen reader, a crawler or a
  // no-CSS render sees the pitch and the form first, and the CSS order
  // moves the picture right only where the grid exists to hold it.
  const body = document.createElement('div')
  body.className = 'pc-popup-body'
  body.dataset.slot = 'content'
  card.appendChild(body)

  if (media) card.appendChild(media)
  return card
}

/**
 * The pitch above the fields. Uses `.pc-popup-sub` (a left-aligned `<p>`)
 * rather than the heading/description pair, which is centred for the
 * standalone main screen.
 */
function buildPitch(copy: { heading: string; description: string }): HTMLElement[] {
  const heading = document.createElement('h2')
  heading.className = 'pc-popup-heading'
  heading.textContent = copy.heading
  const nodes: HTMLElement[] = [heading]
  if (copy.description) {
    const desc = document.createElement('p')
    desc.className = 'pc-popup-sub'
    desc.textContent = copy.description
    nodes.push(desc)
  }
  return nodes
}

function renderMain(design: PopupDesign, fields: PopupField[], opts: MountPopupOptions): HTMLDivElement {
  const card = buildCard(design)
  const body = card.querySelector('[data-slot="content"]') as HTMLElement

  const main = document.createElement('div')
  main.className = 'pc-popup-main'

  const split = design.layout === 'split'
  if (!split && design.main.imageUrl) {
    const safeImage = sanitizePopupUrl(design.main.imageUrl)
    if (safeImage) {
      const img = document.createElement('img')
      img.className = 'pc-popup-image'
      img.src = safeImage
      img.alt = ''
      img.loading = 'lazy'
      img.decoding = 'async'
      img.referrerPolicy = 'no-referrer'
      main.appendChild(img)
    }
  }

  const heading = document.createElement('h2')
  heading.className = 'pc-popup-heading'
  heading.textContent = design.main.heading
  main.appendChild(heading)

  if (design.main.description) {
    const desc = document.createElement('p')
    desc.className = 'pc-popup-description'
    desc.textContent = design.main.description
    main.appendChild(desc)
  }

  const next = document.createElement('button')
  next.type = 'button'
  next.className = 'pc-popup-submit'
  next.style.marginTop = '20px'
  next.textContent = 'Để lại thông tin'
  next.addEventListener('click', () => {
    // A popup with no fields still has to do something useful on its
    // primary action — drop the visitor into chat rather than a dead end.
    if (fields.length === 0) {
      opts.onOpenChat?.()
      return
    }
    current = 'form'
    paint()
  })
  main.appendChild(next)

  if (design.form.secondaryLabel && design.form.secondaryAction) {
    main.appendChild(buildSecondary(design, opts, close))
  }

  body.appendChild(main)
  return card
}

function buildSecondary(
  design: PopupDesign,
  opts: MountPopupOptions,
  close: () => void,
): HTMLButtonElement {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'pc-popup-secondary'
  button.style.marginTop = '10px'
  button.textContent = design.form.secondaryLabel ?? ''
  button.addEventListener('click', () => {
    const action = design.form.secondaryAction
    if (action === 'open_chat') {
      opts.onOpenChat?.()
    } else if (action === 'open_url') {
      const url = sanitizePopupUrl(design.form.secondaryUrl ?? '')
      if (url) opts.onOpenUrl?.(url)
    } else {
      close()
    }
  })
  return button
}

function renderForm(
  design: PopupDesign,
  fields: PopupField[],
  opts: MountPopupOptions,
  close: () => void,
): HTMLDivElement {
  const card = buildCard(design)
  const body = card.querySelector('[data-slot="content"]') as HTMLElement

  const form = document.createElement('form')
  form.className = 'pc-popup-form'
  form.noValidate = true

  // Restate the offer above the fields. On `split` this is what the picture
  // is illustrating, so a form screen without it reads as a second, unlabelled
  // step; on `stacked` it is the same sentence the visitor just chose.
  for (const node of buildPitch(design.main)) form.appendChild(node)

  for (const field of fields) form.appendChild(buildField(field))

  const error = document.createElement('div')
  error.className = 'pc-popup-error'
  error.hidden = true
  error.style.textAlign = 'center'
  error.style.marginTop = '10px'
  form.appendChild(error)

  const actions = document.createElement('div')
  actions.className = 'pc-popup-actions'

  const submit = document.createElement('button')
  submit.type = 'submit'
  submit.className = 'pc-popup-submit'
  submit.textContent = design.form.submitLabel || 'Gửi thông tin'
  actions.appendChild(submit)

  if (design.form.secondaryLabel && design.form.secondaryAction) {
    actions.appendChild(buildSecondary(design, opts, close))
  }
  form.appendChild(actions)

  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const values = readValues(form, fields)
    const found: PopupFieldError[] = []
    for (const field of fields) {
      const message = validatePopupField(field, values[field.key] ?? '')
      if (message) found.push({ fieldKey: field.key, message })
    }
    lastErrors = found
    paintErrors(form, found)
    if (found.length > 0) {
      error.textContent = 'Vui lòng kiểm tra lại các trường được đánh dấu.'
      error.hidden = false
      return
    }
    submit.disabled = true
    submit.textContent = 'Đang gửi...'
    void Promise.resolve(opts.onSubmit(values))
      .then(() => {
        // The lead is captured server-side by the time this resolves, so
        // the visitor can be told it worked.
        showThanks()
      })
      .catch(() => {
        // Let the host surface its own failure UI; re-enable so a retry
        // is possible instead of leaving a permanently dead button.
        submit.disabled = false
        submit.textContent = design.form.submitLabel || 'Gửi thông tin'
        error.textContent = 'Gửi không thành công. Vui lòng thử lại.'
        error.hidden = false
      })
  })

  body.appendChild(form)
  return card
}

function renderThanks(design: PopupDesign, close: () => void): HTMLDivElement {
  const card = buildCard(design)
  const body = card.querySelector('[data-slot="content"]') as HTMLElement

  const thanks = document.createElement('div')
  thanks.className = 'pc-popup-thanks'

  const heading = document.createElement('h2')
  heading.className = 'pc-popup-heading'
  heading.textContent = design.thanks.heading
  thanks.appendChild(heading)

  if (design.thanks.description) {
    const desc = document.createElement('p')
    desc.className = 'pc-popup-description'
    desc.textContent = design.thanks.description
    thanks.appendChild(desc)
  }

  const done = document.createElement('button')
  done.type = 'button'
  done.className = 'pc-popup-submit'
  done.style.marginTop = '20px'
  done.textContent = 'Đóng'
  done.addEventListener('click', () => {
    close()
  })
  thanks.appendChild(done)

  body.appendChild(thanks)
  return card
}

/**
 * The idle screen, shown after `NUDGE_IDLE_MS` of silence on the form.
 *
 * It replaces the form rather than sitting on top of it: a visitor who has
 * stopped typing is not going to read a question layered over the half-filled
 * fields they walked away from. Nothing they typed is lost — going back
 * repaints the same inputs.
 */
function renderNudge(
  design: PopupDesign,
  opts: MountPopupOptions,
  onBack: () => void,
  close: () => void,
): HTMLDivElement {
  const card = buildCard(design)
  const body = card.querySelector('[data-slot="content"]') as HTMLElement

  const nudge = document.createElement('div')
  nudge.className = 'pc-popup-thanks'

  const heading = document.createElement('h2')
  heading.className = 'pc-popup-heading'
  heading.textContent = 'Anh chị có còn đó không ạ?'
  nudge.appendChild(heading)

  // The owner's own copy for the offer, not a second string to keep in sync.
  if (design.main.description) {
    const desc = document.createElement('p')
    desc.className = 'pc-popup-description'
    desc.textContent = design.main.description
    nudge.appendChild(desc)
  }

  const back = document.createElement('button')
  back.type = 'button'
  back.className = 'pc-popup-submit'
  back.style.marginTop = '20px'
  back.textContent = 'Điền tiếp'
  back.addEventListener('click', onBack)
  nudge.appendChild(back)

  // Reuses the form's secondary CTA, so a popup that routes to chat still can
  // from the nudge, and one configured to close still closes.
  if (design.form.secondaryLabel && design.form.secondaryAction) {
    nudge.appendChild(buildSecondary(design, opts, close))
  }

  body.appendChild(nudge)
  return card
}

  function paintErrors(scope: ParentNode, found: PopupFieldError[]): void {
    for (const wrap of Array.from(scope.querySelectorAll('.pc-popup-field'))) {
      const key = (wrap as HTMLElement).dataset.fieldKey
      const el = wrap.querySelector('.pc-popup-error') as HTMLElement | null
      const message = found.find((f) => f.fieldKey === key)?.message
      if (!el) continue
      if (message) {
        el.textContent = message
        el.hidden = false
      } else {
        el.textContent = ''
        el.hidden = true
      }
    }
  }

  function paint(): void {
    teardown()
    backdrop = document.createElement('div')
    backdrop.className = 'pc-popup-backdrop'
    backdrop.setAttribute('role', 'dialog')
    backdrop.setAttribute('aria-modal', 'true')
    backdrop.setAttribute('aria-label', popup.name || 'Popup')

    if (design.closeOnBackdrop) {
      backdrop.addEventListener('click', (event) => {
        if (event.target !== backdrop) return
        close()
      })
    }

    const card =
      current === 'main'
        ? renderMain(design, fields, opts)
        : current === 'form'
          ? renderForm(design, fields, opts, close)
          : current === 'nudge'
            ? renderNudge(
                design,
                opts,
                () => {
                  current = 'form'
                  paint()
                },
                close,
              )
            : renderThanks(design, close)

    const closeButton = document.createElement('button')
    closeButton.type = 'button'
    closeButton.className = 'pc-popup-close'
    closeButton.setAttribute('aria-label', 'Đóng')
    closeButton.textContent = '×'
    closeButton.addEventListener('click', () => {
      close()
    })

    // Inside the card, not the backdrop: the backdrop spans the viewport, so
    // a button anchored to it parks itself in the screen corner, nowhere near
    // a centred card. On `split` the card paints it over the media column.
    card.appendChild(closeButton)
    backdrop.appendChild(card)
    host.appendChild(backdrop)

    // Only the form has something to lose by being interrupted, so the nudge
    // is armed there and nowhere else. The delegated `input` listener rides on
    // the backdrop, which is torn down and rebuilt with the card.
    if (current === 'form') {
      backdrop.addEventListener('input', armNudge)
      armNudge()
    }
  }

  function showThanks(): void {
    if (destroyed) return
    current = 'thanks'
    paint()
  }

  return {
    show() {
      if (destroyed) return
      current = initialScreen()
      paint()
    },
    hide() {
      teardown()
    },
    showThanks,
    screen() {
      return current
    },
    errors() {
      return lastErrors
    },
    destroy() {
      destroyed = true
      teardown()
      if (style.parentNode) style.parentNode.removeChild(style)
    },
  }
}

/**
 * Preview copy for the dashboard editor — the same markup an owner sees,
 * minus the submit plumbing. Exported so the editor does not hand-roll a
 * second render of a form it will later ship.
 */
export function popupSummaryHtml(popup: PopupPublicDto): string {
  return [
    `<div class="pc-popup-heading">${escapeHtml(popup.design.main.heading)}</div>`,
    `<div class="pc-popup-description">${escapeHtml(popup.design.main.description)}</div>`,
  ].join('')
}
